/**
 * Admin Hub Tree Navigation Component
 * Renders the navigation tree with expand/collapse support
 */
import React, { useCallback } from 'react';
import { Box, Typography, Tooltip, Badge, Collapse, Button } from '@mui/material';
import {
  ExpandMore as ExpandIcon,
  ChevronRight as CollapseIcon,
  SearchOff as NoResultsIcon,
} from '@mui/icons-material';
import { useAdminHub } from './context/AdminHubContext';
import { ADMIN_HUB_DESIGN, type TreeNavItem } from '../../config/adminHubConfig';

interface AdminHubTreeNavProps {
  collapsed: boolean;
}

interface TreeItemProps {
  item: TreeNavItem;
  level: number;
  sidebarCollapsed: boolean;
  activePath: string | null;
  expandedNodes: string[];
  onToggle: (nodeId: string) => void;
  onNavigate: (path: string) => void;
}

const TreeItem: React.FC<TreeItemProps> = ({
  item,
  level,
  sidebarCollapsed,
  activePath,
  expandedNodes,
  onToggle,
  onNavigate,
}) => {
  const hasChildren = item.children && item.children.length > 0;
  const isExpanded = expandedNodes.includes(item.id);
  // Compare base paths so tab/query variations of a page still highlight its nav entry
  const isActive = !!item.path
    && !!activePath
    && activePath.split('?')[0] === item.path.split('?')[0];
  const indent = level * ADMIN_HUB_DESIGN.TREE_INDENT;

  const handleClick = useCallback(() => {
    if (item.path) {
      onNavigate(item.path);
      // Navigable parents (section headers) also expand so the destination's
      // siblings become visible
      if (hasChildren && !isExpanded) {
        onToggle(item.id);
      }
    } else if (hasChildren) {
      onToggle(item.id);
    }
  }, [item.path, item.id, hasChildren, isExpanded, onNavigate, onToggle]);

  const handleExpandClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onToggle(item.id);
  }, [item.id, onToggle]);

  // Icon rendering - wrap in Box to apply styles without cloneElement issues
  const renderIcon = () => {
    if (!item.icon) return null;
    return (
      <Box
        component="span"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 20,
          color: isActive ? ADMIN_HUB_DESIGN.COLORS.ACTIVE_ITEM : 'text.secondary',
          flexShrink: 0,
          '& .MuiSvgIcon-root': {
            fontSize: 'inherit',
            color: 'inherit',
          },
        }}
      >
        {item.icon}
      </Box>
    );
  };

  // Subheaders are non-interactive group labels; their children render inline at the
  // same level, so grouping adds no click or indent depth
  if (item.type === 'subheader') {
    if (sidebarCollapsed) return null;
    return (
      <>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            pl: `${8 + indent + 24}px`,
            pr: 1,
            pt: 1.5,
            pb: 0.25,
            color: ADMIN_HUB_DESIGN.COLORS.SECTION_HEADER,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            fontSize: '0.6875rem',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {item.label}
        </Typography>
        {item.children?.map(child => (
          <TreeItem
            key={child.id}
            item={child}
            level={level}
            sidebarCollapsed={sidebarCollapsed}
            activePath={activePath}
            expandedNodes={expandedNodes}
            onToggle={onToggle}
            onNavigate={onNavigate}
          />
        ))}
      </>
    );
  }

  // For collapsed sidebar, show only top-level icons with tooltips
  if (sidebarCollapsed && level === 0) {
    return (
      <Tooltip title={item.label} placement="right">
        <Box
          onClick={handleClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: ADMIN_HUB_DESIGN.TREE_ITEM_HEIGHT,
            cursor: 'pointer',
            backgroundColor: isActive ? ADMIN_HUB_DESIGN.COLORS.ACTIVE_ITEM_BG : 'transparent',
            borderLeft: isActive ? `3px solid ${ADMIN_HUB_DESIGN.COLORS.ACTIVE_ITEM}` : '3px solid transparent',
            '&:hover': {
              backgroundColor: isActive
                ? ADMIN_HUB_DESIGN.COLORS.ACTIVE_ITEM_BG
                : ADMIN_HUB_DESIGN.COLORS.HOVER_BG,
            },
            transition: 'background-color 150ms ease',
          }}
        >
          {item.badge ? (
            <Badge
              badgeContent={item.badge.count}
              color={item.badge.color}
              max={99}
            >
              {renderIcon()}
            </Badge>
          ) : (
            renderIcon()
          )}
        </Box>
      </Tooltip>
    );
  }

  // Don't render nested items when sidebar is collapsed
  if (sidebarCollapsed && level > 0) {
    return null;
  }

  return (
    <>
      <Box
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: ADMIN_HUB_DESIGN.TREE_ITEM_HEIGHT,
          pl: `${8 + indent}px`,
          pr: 1,
          cursor: 'pointer',
          backgroundColor: isActive ? ADMIN_HUB_DESIGN.COLORS.ACTIVE_ITEM_BG : 'transparent',
          borderLeft: isActive ? `3px solid ${ADMIN_HUB_DESIGN.COLORS.ACTIVE_ITEM}` : '3px solid transparent',
          '&:hover': {
            backgroundColor: isActive
              ? ADMIN_HUB_DESIGN.COLORS.ACTIVE_ITEM_BG
              : ADMIN_HUB_DESIGN.COLORS.HOVER_BG,
          },
          transition: 'background-color 150ms ease',
        }}
      >
        {/* Expand/collapse button for items with children */}
        {hasChildren ? (
          <Box
            onClick={handleExpandClick}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 20,
              height: 20,
              mr: 0.5,
              borderRadius: 0.5,
              cursor: 'pointer',
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            {isExpanded ? (
              <ExpandIcon sx={{ fontSize: 18 }} />
            ) : (
              <CollapseIcon sx={{ fontSize: 18 }} />
            )}
          </Box>
        ) : (
          <Box sx={{ width: 20, mr: 0.5 }} />
        )}

        {/* Icon */}
        {item.icon && (
          <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
            {item.badge ? (
              <Badge
                badgeContent={item.badge.count}
                color={item.badge.color}
                max={99}
              >
                {renderIcon()}
              </Badge>
            ) : (
              renderIcon()
            )}
          </Box>
        )}

        {/* Label */}
        <Typography
          variant="body2"
          sx={{
            fontWeight: isActive ? 500 : 400,
            color: isActive ? ADMIN_HUB_DESIGN.COLORS.ACTIVE_ITEM : 'text.primary',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            flex: 1,
            fontSize: level === 0 ? '0.875rem' : '0.8125rem',
          }}
        >
          {item.label}
        </Typography>

        {/* Badge (if no icon) */}
        {item.badge && !item.icon && (
          <Badge
            badgeContent={item.badge.count}
            color={item.badge.color}
            max={99}
            sx={{ ml: 1 }}
          />
        )}
      </Box>

      {/* Children */}
      {hasChildren && (
        <Collapse in={isExpanded} timeout={200}>
          <Box>
            {item.children!.map(child => (
              <TreeItem
                key={child.id}
                item={child}
                level={level + 1}
                sidebarCollapsed={sidebarCollapsed}
                activePath={activePath}
                expandedNodes={expandedNodes}
                onToggle={onToggle}
                onNavigate={onNavigate}
              />
            ))}
          </Box>
        </Collapse>
      )}
    </>
  );
};

export const AdminHubTreeNav: React.FC<AdminHubTreeNavProps> = ({ collapsed }) => {
  const { displayNavTree, activePath, expandedNodes, toggleNode, navigateTo, filterText, clearFilter } = useAdminHub();

  // Show "no results" message when filtering returns empty
  if (filterText && displayNavTree.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4,
          px: 2,
          textAlign: 'center',
        }}
      >
        <NoResultsIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          No results for "{filterText}"
        </Typography>
        <Button
          size="small"
          onClick={clearFilter}
          sx={{ textTransform: 'none' }}
        >
          Clear filter
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
      }}
    >
      {displayNavTree.map(item => (
        <TreeItem
          key={item.id}
          item={item}
          level={0}
          sidebarCollapsed={collapsed}
          activePath={activePath}
          expandedNodes={expandedNodes}
          onToggle={toggleNode}
          onNavigate={navigateTo}
        />
      ))}
    </Box>
  );
};

export default AdminHubTreeNav;
